import { Countries } from "constants/countries";
import { stores } from "constants/stores";
import {
  IMusicData,
  ITrack,
  ITrackArtists,
  ITrackSplit
} from "types/musicData";
import {
  ReleaseContainerWithUrls,
  SongWithUrls,
  SplitSheet
} from "types/response-type";
import { audioUrlToFile } from "utils/audioUrlToFile";
import { addDays } from "utils/dateConvert";
import { imageUrlToFile } from "utils/imageUrlToFile";

export const ConvertApiResponseToMusicData = async (
  track: SongWithUrls | ReleaseContainerWithUrls
): Promise<IMusicData> => {
  let musicData: IMusicData = {
    releaseTitle: "",
    recordLabel: "",
    releaseType: "",
    primaryArtist: { id: "", name: "" },
    releaseLanguage: "",
    primaryGenre: "",
    secondaryGenre: "",
    coverImage: undefined,
    trackList: [],
    schedule: {
      trackPrice: 0,
      originalReleaseDate: new Date(),
      proposedReleaseDate: addDays(new Date(), 7),
      preorderDate: new Date(),
      countries: [...Countries],
      releaseTime: "12:00"
    },
    stores: [...stores]
  };

  const isSongWithUrls = (
    data: SongWithUrls | ReleaseContainerWithUrls
  ): data is SongWithUrls => {
    return "releaseType" in data;
  };

  //change to include container

  const isReleaseContainerWithUrls = (
    data: SongWithUrls | ReleaseContainerWithUrls
  ): data is ReleaseContainerWithUrls => {
    return "type" in data && "tracks" in data;
  };

  let trackList: ITrack[] = [];
  let targetCountryObjects: { country: string; continent: string }[] = [];

  if (isSongWithUrls(track)) {
    let split: ITrackSplit[] = [];
    if (track.currentSplitSheet) {
      split = convertSplitSheetToITrackSplit(track.currentSplitSheet);
    }

    const file = await audioUrlToFile(track.masterTrackPath, track.title);

    trackList = [
      {
        file: file,
        trackDetails: {
          title: track.title,
          recordingYear: track.recordingYear.toString(),
          language: track.releaseLanguage
        },
        trackArtists: [track.primaryArtist as unknown as ITrackArtists],
        // contributors: track.contributors,
        // songWriters: track.,
        // split: track.split,
        isrc: track.isrc || "",
        previewClip: {
          startTime: 0,
          endTime: 0
        },
        lyrics: {
          isExplicit: false,
          text: ""
        },
        split: split,
        splitSheetId: track?.currentSplitSheetId || undefined
      }
    ];

    // Map targetCountries to country objects with continents
    targetCountryObjects = track.targetCountries.map((countryName) => {
      const countryObj = Countries.find(
        (c: { country: string; continent: string }) => c.country === countryName
      );
      return countryObj || { country: countryName, continent: "Unknown" };
    });
  } else if (isReleaseContainerWithUrls(track)) {
    if (track.tracks && track.tracks.length > 0) {
      trackList = await Promise.all(
        track.tracks.map(async (song) => {
          let split: ITrackSplit[] = [];
          if (song.currentSplitSheet) {
            split = convertSplitSheetToITrackSplit(song.currentSplitSheet);
          }

          const file = await audioUrlToFile(song.masterTrackPath, song.title);

          return {
            file: file,
            trackDetails: {
              title: song.title,
              recordingYear: song.recordingYear.toString(),
              language: song.releaseLanguage
            },
            trackArtists: [song.primaryArtist as unknown as ITrackArtists],
            isrc: song.isrc || "",
            previewClip: {
              startTime: 0,
              endTime: 0
            },
            lyrics: {
              isExplicit: false,
              text: ""
            },
            split: split,
            splitSheetId: song.currentSplitSheetId || undefined
          };
        })
      );

      targetCountryObjects = track.tracks[0]?.targetCountries.map(
        (countryName) => {
          const countryObj = Countries.find(
            (c: { country: string; continent: string }) =>
              c.country === countryName
          );
          return countryObj || { country: countryName, continent: "Unknown" };
        }
      );
    }
  }

  if (isSongWithUrls(track)) {
    const coverImage = await imageUrlToFile(
      track.coverArtPath,
      "cover-art.jpg"
    );

    const MusicData = {
      releaseTitle: track.title,
      recordLabel: track.label,
      releaseType: track.releaseType,
      primaryArtist: track.primaryArtist,
      releaseLanguage: track.releaseLanguage,
      primaryGenre: track.primaryGenre,
      secondaryGenre: track.secondaryGenres[0] || "",
      coverImage: coverImage,
      trackList: trackList,
      schedule: {
        originalReleaseDate: new Date(track.originalReleaseDate || ""),
        proposedReleaseDate: new Date(track.proposedReleaseDate || ""),
        releaseTime: track.releaseTime,
        preorderDate: new Date(track.preOrderDate || ""),
        trackPrice: Number(track.trackPrice),
        countries: targetCountryObjects
      },
      stores: track.targetStores
    };

    musicData = MusicData;
  } else if (isReleaseContainerWithUrls(track)) {
    const firstTrack = track.tracks[0];

    const coverImage = await imageUrlToFile(
      track.coverArtPath,
      "cover-art.jpg"
    );

    const MusicData = {
      releaseTitle: track.title,
      recordLabel: firstTrack?.label || "",
      releaseType: track.type,
      primaryArtist: firstTrack?.primaryArtist,
      releaseLanguage: firstTrack?.releaseLanguage || "",
      primaryGenre: firstTrack?.primaryGenre || "",
      secondaryGenre: firstTrack?.secondaryGenres?.[0] || "",
      coverImage: coverImage,
      trackList: trackList,
      schedule: {
        originalReleaseDate: firstTrack?.originalReleaseDate
          ? new Date(firstTrack.originalReleaseDate)
          : undefined,
        proposedReleaseDate: new Date(track.releaseDate || ""),
        releaseTime: firstTrack?.releaseTime || "12:00",
        preorderDate: firstTrack?.preOrderDate
          ? new Date(firstTrack.preOrderDate)
          : undefined,
        trackPrice: firstTrack?.trackPrice ? Number(firstTrack.trackPrice) : 0,
        countries: targetCountryObjects
      },
      stores: firstTrack?.targetStores || []
    };

    musicData = MusicData;
  }

  return musicData;
};

export const convertSplitSheetToITrackSplit = (splitSheet: SplitSheet) => {
  if (!splitSheet.entries) {
    return [];
  }

  return splitSheet?.entries
    .map((entry) => {
      return {
        id: entry.id,
        recipientName: entry.recipientEmail,
        recipientEmail: entry.recipientEmail,
        percentage: Number(entry.percentage) * (100 / 85)
      };
    })
    .filter((entry) => entry?.recipientEmail !== "Platform Service Fee");
};
